import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import '../styles/superadmin.css';

const ManageProjectLinks = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('saas_superadmin_token');
    const [copiedLink, setCopiedLink] = useState(null);

    // Redirect if not authenticated
    if (!token) {
        navigate('/superadmin_v2_secure_login');
        return null;
    }

    const BASE_URL = window.location.origin;

    const linkGroups = [
        {
            title: 'Public Endpoints',
            icon: 'fa-globe',
            links: [
                { name: 'Landing Page', path: '/' },
                { name: 'Terms and Conditions', path: '/terms' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Refund Policy', path: '/refund-policy' },
                { name: 'Contact Us', path: '/contact' }
            ]
        },
        {
            title: 'Restaurant & Admin Portals',
            icon: 'fa-store',
            links: [
                { name: 'Restaurant Login', path: '/login' },
                { name: 'Restaurant Registration', path: '/signup' },
                { name: 'Admin Dashboard', path: '/manage_v2_secure_auth' },
                { name: 'Example Menu Link', path: '/your_username', isDynamic: true }
            ]
        },
        {
            title: 'Sponsor Portal',
            icon: 'fa-handshake',
            links: [
                { name: 'Sponsor Login / Dashboard', path: '/sponsor-portal' }
            ]
        },
        {
            title: 'Kitchen & Staff Portals',
            icon: 'fa-utensils',
            links: [
                { name: 'Staff Login', path: '/staff-login' },
                { name: 'Kitchen Dashboard', path: '/kitchen-dashboard' },
                { name: 'Delivery Dashboard', path: '/delivery-dashboard' }
            ]
        },
        {
            title: 'Superadmin Portals',
            icon: 'fa-user-shield',
            links: [
                { name: 'Superadmin Login', path: '/superadmin_v2_secure_login' },
                { name: 'Superadmin Dashboard', path: '/superadmin_v2_secure' }
            ]
        },
        {
            title: 'Demo Menus',
            icon: 'fa-play-circle',
            links: [
                { name: 'Demo Kitchen 1', path: '/demo-kitchen' },
                { name: 'Demo Kitchen 2', path: '/demokitchen' },
                { name: 'Test Cafe', path: '/testcafe' }
            ]
        },
        {
            title: 'Legacy Portals (PHP-based)',
            icon: 'fa-server',
            links: [
                { name: 'Legacy Admin Panel', path: 'https://menu.searchisend.in/admin/', isExternal: true },
                { name: 'Legacy Superadmin Panel', path: 'https://menu.searchisend.in/superadmin/', isExternal: true }
            ]
        }
    ];

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedLink(text);
            setTimeout(() => setCopiedLink(null), 2000);
        }).catch(err => {
            console.error('Failed to copy!', err);
        });
    };

    return (
        <SuperAdminLayout>
            <div className="sa-container">
                <div className="sa-header-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 className="sa-page-title">
                        <i className="fas fa-link" style={{ marginRight: '10px', color: 'var(--accent-primary)' }}></i>
                        Project Links Directory
                    </h2>
                </div>

                <div className="sa-panel" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
                        {linkGroups.map((group, gIndex) => (
                            <div key={gIndex} className="sa-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column' }}>
                                <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--text-primary)', marginBottom: '15px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                                    <i className={`fas ${group.icon}`} style={{ color: 'var(--accent-primary)', marginRight: '8px' }}></i>
                                    {group.title}
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {group.links.map((link, lIndex) => {
                                        const fullUrl = link.isExternal ? link.path : `${BASE_URL}${link.path}`;
                                        
                                        return (
                                            <div key={lIndex} style={{ display: 'flex', flexDirection: 'column', gap: '5px', background: 'var(--bg-card)', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span style={{ fontWeight: '600', fontSize: '0.9rem', color: 'var(--text-primary)' }}>{link.name}</span>
                                                    <button 
                                                        onClick={() => copyToClipboard(fullUrl)}
                                                        style={{ 
                                                            background: 'transparent', 
                                                            border: 'none', 
                                                            color: copiedLink === fullUrl ? '#10b981' : 'var(--text-secondary)',
                                                            cursor: 'pointer',
                                                            transition: 'color 0.2s'
                                                        }}
                                                        title="Copy URL"
                                                    >
                                                        <i className={copiedLink === fullUrl ? "fas fa-check" : "far fa-copy"}></i>
                                                    </button>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {fullUrl}
                                                    </span>
                                                    {link.isExternal ? (
                                                        <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)', fontSize: '0.8rem', flexShrink: 0 }}>
                                                            <i className="fas fa-external-link-alt"></i>
                                                        </a>
                                                    ) : (
                                                        <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)', fontSize: '0.8rem', flexShrink: 0 }}>
                                                            <i className="fas fa-arrow-right"></i>
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </SuperAdminLayout>
    );
};

export default ManageProjectLinks;
